import { CheckIcon } from '@heroicons/react/16/solid';
import {
  type FilterOperator,
  adjustValuesForOperator,
  getMenuOperators,
  isNegationOperator,
  menuOperatorMatches,
  textOperatorForValues,
} from 'admin/src/components/ui/DataGrid/filters/operators';
import type { FilterableColumn } from 'admin/src/components/ui/DataGrid/filters/types';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Command, CommandItem, CommandList, Popover, PopoverContent, PopoverTrigger } from 'shared/src/components/ui';
import { cn } from 'shared/src/utils';

type FilterOperatorMenuProps = {
  column: FilterableColumn;
  operator: FilterOperator;
  values: string[];
  fallbackSecondValue?: string;
  children: ReactNode;
  onChange: (operator: FilterOperator, values: string[]) => void;
};

export function FilterOperatorMenu({ column, operator, values, fallbackSecondValue, children, onChange }: FilterOperatorMenuProps) {
  const [open, setOpen] = useState(false);
  const operators = getMenuOperators(column.dataType);

  function select(next: FilterOperator) {
    setOpen(false);

    if (column.dataType === 'text') {
      const normalized = textOperatorForValues(isNegationOperator(next), values.length);
      if (normalized === operator) return;
      onChange(normalized, values);
      return;
    }

    if (next === operator) return;
    onChange(next, adjustValuesForOperator(column.dataType, next, values, fallbackSecondValue));
  }

  return (
    <Popover modal={false} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent withPortal align="start" className="w-auto min-w-[180px] p-0" onOpenAutoFocus={(event) => event.preventDefault()}>
        <Command className="origin-[var(--radix-popover-content-transform-origin)]">
          <CommandList
            className="overscroll-contain touch-pan-y p-1 [-webkit-overflow-scrolling:touch]"
            onWheel={(event) => event.stopPropagation()}
            onTouchMove={(event) => event.stopPropagation()}
          >
            {operators.map((detail) => (
              <CommandItem key={detail.value} value={detail.value} onSelect={() => select(detail.value)} className="gap-2 text-xs">
                <CheckIcon className={cn('size-3.5 shrink-0', menuOperatorMatches(column.dataType, operator, detail.value) ? 'opacity-100' : 'opacity-0')} />
                {detail.label}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
